// Page publique : comparatif détaillé des 3 formules (Solo / Équipe / Structure).
// Reprend les prix de la vitrine (mensuel + annuel -10%).
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Check, Minus } from "lucide-react";

const LOGO = "/logo_GAP.jpg";

const fmt = (n) => Math.round(n || 0).toLocaleString("fr-FR");
const prixAnnuel = (mensuel) => Math.round(mensuel * 12 * 0.9);

const FORMULES = [
  { code: "solo", libelle: "Solo", description: "TPE 1 à 5 personnes", prixMensuel: 3500, populaire: false },
  { code: "equipe", libelle: "Équipe", description: "PME 6 à 20 personnes", prixMensuel: 6500, populaire: true },
  { code: "structure", libelle: "Structure", description: "PME 20 personnes et plus", prixMensuel: 12000, populaire: false },
];

// Lignes de comparaison : valeur par formule (true = inclus, false = non, ou texte).
const CARACTERISTIQUES = [
  {
    groupe: "Utilisateurs & accès",
    lignes: [
      { label: "Nombre d'utilisateurs", solo: "1 utilisateur", equipe: "Jusqu'à 5", structure: "Illimité" },
      { label: "Gestion des rôles (RBAC)", solo: false, equipe: true, structure: true },
      { label: "Invitations par email", solo: false, equipe: true, structure: true },
      { label: "Multi-sociétés", solo: false, equipe: false, structure: true },
    ],
  },
  {
    groupe: "Saisie comptable",
    lignes: [
      { label: "Tous les journaux (VT, AC, BQ, CA, OD, ODS)", solo: true, equipe: true, structure: true },
      { label: "Facturation client & fournisseur", solo: true, equipe: true, structure: true },
      { label: "Duplication d'écritures récurrentes", solo: true, equipe: true, structure: true },
      { label: "Trésorerie & arrêté de caisse", solo: true, equipe: true, structure: true },
      { label: "Rapprochement bancaire", solo: false, equipe: true, structure: true },
      { label: "Lettrage des comptes tiers", solo: true, equipe: true, structure: true },
    ],
  },
  {
    groupe: "États & déclarations",
    lignes: [
      { label: "Balance, grand livre, journaux", solo: true, equipe: true, structure: true },
      { label: "Bilan & compte de résultat", solo: true, equipe: true, structure: true },
      { label: "Déclaration TVA DICP 1010", solo: true, equipe: true, structure: true },
      { label: "Exports CSV / XLS / PDF", solo: true, equipe: true, structure: true },
      { label: "Factures client imprimables (PDF)", solo: true, equipe: true, structure: true },
    ],
  },
  {
    groupe: "Sécurité & support",
    lignes: [
      { label: "Sauvegarde locale chiffrée", solo: true, equipe: true, structure: true },
      { label: "Données chiffrées & sauvegardées", solo: true, equipe: true, structure: true },
      { label: "Support par email", solo: true, equipe: true, structure: true },
      { label: "Support prioritaire", solo: false, equipe: true, structure: true },
      { label: "Accompagnement dédié", solo: false, equipe: false, structure: true },
    ],
  },
  {
    groupe: "Options",
    lignes: [
      { label: "Module Immobilisations (add-on 6 000 XPF/mois)", solo: true, equipe: true, structure: true },
    ],
  },
];

function Cellule({ valeur }) {
  if (valeur === true) {
    return <span className="inline-flex"><Check className="h-5 w-5 text-[#4A8A8A]" aria-label="Inclus" /></span>;
  }
  if (valeur === false) {
    return <span className="inline-flex"><Minus className="h-4 w-4 text-[#1D3D3D]/25" aria-label="Non inclus" /></span>;
  }
  return <span className="text-sm font-medium text-[#1D3D3D]">{valeur}</span>;
}

export default function ComparatifFormules() {
  const [periodicite, setPeriodicite] = useState("mensuel");

  return (
    <div data-testid="page-comparatif" className="min-h-screen bg-white text-[#1D3D3D] flex flex-col">
      <header className="border-b border-[#1D3D3D]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO} alt="Groupe AERH Polynésie — Porinetia Compta AERH" className="h-8 w-auto rounded" />
            <span className="text-base font-extrabold tracking-tight">Porinetia Compta AERH</span>
          </Link>
          <Button asChild variant="outline" className="border-[#1D3D3D]/20 text-[#1D3D3D]">
            <Link to="/" data-testid="retour-accueil-link">
              <ArrowLeft className="h-4 w-4 mr-1" /> Retour à l'accueil
            </Link>
          </Button>
        </div>
      </header>

      <main className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight">Comparatif des formules</h1>
          <p className="mt-4 text-base text-[#1D3D3D]/70 max-w-2xl mx-auto">
            Choisissez la formule adaptée à la taille de votre entreprise. 30 jours gratuits, sans carte bancaire.
          </p>
          <div className="mt-6 inline-flex items-center rounded-full bg-[#E8F4F4] p-1">
            <button
              type="button"
              data-testid="comparatif-toggle-mensuel"
              onClick={() => setPeriodicite("mensuel")}
              className={`px-5 h-9 rounded-full text-sm font-semibold transition-colors ${periodicite === "mensuel" ? "bg-[#1D3D3D] text-white" : "text-[#1D3D3D]"}`}
            >
              Mensuel
            </button>
            <button
              type="button"
              data-testid="comparatif-toggle-annuel"
              onClick={() => setPeriodicite("annuel")}
              className={`px-5 h-9 rounded-full text-sm font-semibold transition-colors ${periodicite === "annuel" ? "bg-[#1D3D3D] text-white" : "text-[#1D3D3D]"}`}
            >
              Annuel <span className="text-emerald-500">-10%</span>
            </button>
          </div>
        </div>

        <div className="mt-10 overflow-x-auto">
          <table className="w-full min-w-[680px] border-collapse">
            <thead>
              <tr>
                <th className="text-left p-4 align-bottom w-[34%]" />
                {FORMULES.map((f) => (
                  <th key={f.code} data-testid={`comparatif-col-${f.code}`} className="p-4 align-bottom text-center">
                    <div className={`rounded-2xl p-5 ${f.populaire ? "bg-[#1D3D3D] text-white shadow-lg" : "bg-[#E8F4F4] text-[#1D3D3D]"}`}>
                      {f.populaire && <span className="inline-block mb-2 px-2.5 py-0.5 rounded-full bg-[#4A8A8A] text-white text-[11px] font-semibold">Le plus choisi</span>}
                      <p className="text-lg font-black uppercase tracking-wide">{f.libelle}</p>
                      <p className={`text-xs ${f.populaire ? "text-white/70" : "text-[#1D3D3D]/60"}`}>{f.description}</p>
                      <p className="mt-3">
                        <span data-testid={`comparatif-prix-${f.code}`} className="text-3xl font-black">
                          {periodicite === "mensuel" ? fmt(f.prixMensuel) : fmt(prixAnnuel(f.prixMensuel))}
                        </span>
                        <span className={`text-xs ${f.populaire ? "text-white/70" : "text-[#1D3D3D]/70"}`}> XPF / {periodicite === "mensuel" ? "mois" : "an"}</span>
                      </p>
                      <Button
                        asChild
                        data-testid={`comparatif-choisir-${f.code}`}
                        className={`mt-4 h-10 w-full font-semibold rounded-md ${f.populaire ? "bg-white text-[#1D3D3D] hover:bg-white/90" : "bg-[#1D3D3D] text-white hover:bg-[#2D5A5A]"}`}
                      >
                        <Link to="/register">Essai gratuit</Link>
                      </Button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {CARACTERISTIQUES.map((g) => (
                <React.Fragment key={g.groupe}>
                  <tr>
                    <td colSpan={4} className="pt-8 pb-2 px-4">
                      <h2 className="text-sm font-bold uppercase tracking-wide text-[#4A8A8A]">{g.groupe}</h2>
                    </td>
                  </tr>
                  {g.lignes.map((l) => (
                    <tr key={l.label} className="border-t border-[#4A8A8A]/15">
                      <td className="p-4 text-sm text-[#1D3D3D]/90">{l.label}</td>
                      <td className="p-4 text-center"><Cellule valeur={l.solo} /></td>
                      <td className="p-4 text-center bg-[#E8F4F4]/40"><Cellule valeur={l.equipe} /></td>
                      <td className="p-4 text-center"><Cellule valeur={l.structure} /></td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-12 text-center">
          <Button asChild data-testid="comparatif-cta-register" className="h-12 px-8 bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white font-bold rounded-md">
            <Link to="/register">Commencer mon essai gratuit</Link>
          </Button>
          <p className="mt-3 text-sm text-[#1D3D3D]/60">
            Une question sur les formules ?{" "}
            <Link to="/contact" className="font-semibold text-[#1D3D3D] hover:text-[#4A8A8A]">Contactez-nous</Link>.
          </p>
        </div>
      </main>

      <footer className="border-t border-[#1D3D3D]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-xs text-[#1D3D3D]/60">
          © 2026 Groupe AERH Polynésie — Tous droits réservés
        </div>
      </footer>
    </div>
  );
}
